import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host { display: block; }

    /* ── Container ──────────────────────────────────────────────────────────── */
    .uwc-tree {
      background:    var(--uwc-tree-bg, ${t.surface});
      border:        var(--uwc-tree-border, 1px solid ${t.border});
      border-radius: var(--uwc-tree-radius, ${t.radiusMd});
      overflow: hidden;
      font-size: var(--uwc-tree-node-font-size, ${t.fontSizeMd});
      color: var(--uwc-tree-node-color, ${t.text});
      position: relative;
    }

    /* ── Scroll height ──────────────────────────────────────────────────────── */
    .uwc-tree--scrollable {
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: ${t.border} transparent;
    }
    .uwc-tree--scrollable::-webkit-scrollbar { width: 6px; }
    .uwc-tree--scrollable::-webkit-scrollbar-track { background: transparent; }
    .uwc-tree--scrollable::-webkit-scrollbar-thumb {
      background: ${t.border};
      border-radius: ${t.radiusFull};
    }

    /* ── Filter ─────────────────────────────────────────────────────────────── */
    .uwc-tree__filter {
      padding: ${t.space2} ${t.space3};
      border-bottom: var(--uwc-tree-filter-border, 1px solid ${t.border});
    }
    .uwc-tree__filter uwc-inputtext {
      width: 100%;
    }

    /* ── Loading overlay ────────────────────────────────────────────────────── */
    .uwc-tree__loading {
      position: absolute;
      inset: 0;
      background: rgba(255, 255, 255, 0.75);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10;
      gap: ${t.space2};
    }
    .uwc-tree__spinner {
      display: inline-block;
      width: 1.5rem;
      height: 1.5rem;
      border: 2px solid var(--uwc-tree-loading-color, ${t.border});
      border-top-color: var(--uwc-color-primary, ${t.primary});
      border-radius: 50%;
      animation: uwc-tree-spin 600ms linear infinite;
      color: var(--uwc-tree-loading-color, var(--uwc-color-primary, ${t.primary}));
    }

    @keyframes uwc-tree-spin {
      to { transform: rotate(360deg); }
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-tree__spinner,
      .uwc-tree__spinner-icon { animation: none; }
    }

    /* ── Node list (root) ───────────────────────────────────────────────────── */
    ul.uwc-tree__nodes {
      list-style: none;
      margin: 0;
      padding: ${t.space1} 0;
      outline: none;
    }

    /* ── Node list (children) ───────────────────────────────────────────────── */
    ul.uwc-tree__children {
      list-style: none;
      margin: 0;
      padding: 0 0 0 var(--uwc-tree-indent, 1.25rem);
      outline: none;
    }

    /* ── Node item ──────────────────────────────────────────────────────────── */
    .uwc-tree__node {
      display: block;
    }

    /* ── Node content row ───────────────────────────────────────────────────── */
    .uwc-tree__node-content {
      display: flex;
      align-items: center;
      gap: ${t.space1};
      padding: var(--uwc-tree-node-padding, 6px ${t.space2});
      cursor: pointer;
      border-radius: ${t.radiusSm};
      margin: 1px ${t.space1};
      transition: background ${t.durationFast};
      outline: none;
      user-select: none;
      -webkit-user-select: none;
    }

    .uwc-tree__node-content:hover {
      background: var(--uwc-tree-node-hover-bg, ${t.hoverBg});
    }

    .uwc-tree__node-content--selected {
      background: var(--uwc-tree-node-selected-bg, ${t.selectedBg});
      color: var(--uwc-tree-node-selected-color, var(--uwc-color-primary, ${t.primary}));
    }

    .uwc-tree__node-content--selected:hover {
      background: color-mix(in oklab, var(--uwc-tree-node-selected-bg, ${t.selectedBg}) 85%, var(--uwc-color-primary, ${t.primary}));
    }

    .uwc-tree__node-content--focused {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }

    .uwc-tree__node-content--drop-target {
      background: color-mix(in oklab, var(--uwc-color-primary, ${t.primary}) 12%, transparent);
      outline: 2px dashed var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }

    .uwc-tree__node-content--disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }

    /* ── Drag-and-drop cursor ───────────────────────────────────────────────── */
    :host([drag-drop]) .uwc-tree__node-content {
      cursor: grab;
    }
    :host([drag-drop]) .uwc-tree__node-content:active {
      cursor: grabbing;
    }

    /* ── Toggler placeholder (no toggler, just spacing) ─────────────────────── */
    .uwc-tree__toggler-placeholder {
      width: 1.25rem;
      height: 1.25rem;
      flex-shrink: 0;
    }

    /* ── Toggler icon wrapper — drives rotation & loading spin ──────────────── */
    .uwc-tree__toggler-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: var(--uwc-tree-toggler-color, ${t.textSecondary});
      transition: transform ${t.durationBase} ${t.easingStandard};
      transform: rotate(0deg);
    }

    .uwc-tree__toggler-icon--expanded {
      transform: rotate(90deg);
    }

    @media (prefers-reduced-motion: reduce) {
      .uwc-tree__toggler-icon { transition: none; }
    }

    /* ── Node icon ──────────────────────────────────────────────────────────── */
    .uwc-tree__icon {
      flex-shrink: 0;
      color: var(--uwc-tree-icon-color, ${t.textSecondary});
      display: flex;
      align-items: center;
    }

    /* ── Node label ─────────────────────────────────────────────────────────── */
    .uwc-tree__label {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* ── Checkbox wrapper inside node ───────────────────────────────────────── */
    .uwc-tree__checkbox {
      flex-shrink: 0;
      pointer-events: none;
    }

    /* ── Empty state ────────────────────────────────────────────────────────── */
    .uwc-tree__empty {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: ${t.space6} ${t.space4};
      color: var(--uwc-tree-empty-color, ${t.textMuted});
      font-size: ${t.fontSizeSm};
    }
  `,
];
